import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import Seo from '@/components/Seo'
import { useAuth } from '@/hooks/useAuth'
import {
  fetchAllEnrollments,
  statusLabel,
  updateEnrollment,
  type CourseEnrollment,
  type EnrollmentStatus,
} from '@/lib/enrollments'

function Admin() {
  const { isAuthenticated, isAdmin } = useAuth()
  const [rows, setRows] = useState<CourseEnrollment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyId, setBusyId] = useState('')
  const [filter, setFilter] = useState<'all' | EnrollmentStatus>('pending')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const list = await fetchAllEnrollments()
      setRows(list)
    } catch (err) {
      console.error('No se pudieron cargar las inscripciones:', err)
      setError('No se pudieron cargar las solicitudes. Inténtalo de nuevo.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAdmin) void load()
  }, [isAdmin, load])

  if (!isAuthenticated) {
    return <Navigate to="/acceso?next=/admin" replace />
  }

  if (!isAdmin) {
    return <Navigate to="/panel" replace />
  }

  const visible = rows.filter((row) => (filter === 'all' ? true : row.status === filter))

  const act = async (row: CourseEnrollment, updates: Partial<Pick<CourseEnrollment, 'status' | 'documentsAccess'>>) => {
    if (!row._id) return
    setBusyId(row._id)
    setError('')
    try {
      await updateEnrollment(row._id, updates)
      await load()
    } catch (err) {
      console.error('No se pudo actualizar la inscripción:', err)
      setError('No se pudo guardar el cambio. Comprueba que tu cuenta es administradora.')
    } finally {
      setBusyId('')
    }
  }

  return (
    <>
      <Seo title="Administración" description="Revisa solicitudes de inscripción y concede acceso a los documentos de cada curso." />
      <section className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <p className="text-xs uppercase tracking-[0.28em] text-[#C41E3A]">Gestión</p>
        <h1 className="mt-3 font-['Fraunces'] text-4xl">Inscripciones</h1>
        <p className="mt-3 max-w-2xl text-sm text-[#1E3A5F]/70">
          Cuando alguien solicita plaza, puedes aceptarlo en el curso o darle acceso a todos los documentos del aula.
        </p>
        <p className="mt-4 text-sm">
          <Link to="/admin/avance" className="underline">
            Ver avance de alumnos
          </Link>
        </p>

        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Filtrar solicitudes">
          {([
            ['pending', 'Pendientes'],
            ['approved', 'Aceptadas'],
            ['rejected', 'Rechazadas'],
            ['all', 'Todas'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`border px-3 py-1.5 text-sm ${
                filter === value ? 'border-[#1E3A5F] bg-[#1E3A5F] text-[#F4EFE6]' : 'border-[#1E3A5F]/20'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? <p className="mt-4 text-sm text-[#C41E3A]">{error}</p> : null}

        {loading ? (
          <p className="mt-10 text-sm text-[#1E3A5F]/60">Cargando solicitudes…</p>
        ) : visible.length === 0 ? (
          <p className="mt-10 border border-dashed border-[#1E3A5F]/20 p-8 text-sm text-[#1E3A5F]/65">
            No hay solicitudes en esta vista.
          </p>
        ) : (
          <ul className="mt-8 space-y-4">
            {visible.map((row) => (
              <li key={row._id} className="border border-[#1E3A5F]/10 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#C41E3A]">{statusLabel(row.status)}</p>
                    <h2 className="mt-1 font-['Fraunces'] text-2xl">{row.courseTitle || 'Curso'}</h2>
                    <p className="mt-2 text-sm">{row.fullName}</p>
                    <p className="text-sm text-[#1E3A5F]/60">{row.email}</p>
                    {row.phone ? <p className="text-sm text-[#1E3A5F]/60">{row.phone}</p> : null}
                    {row.note ? <p className="mt-2 text-sm text-[#1E3A5F]/70">{row.note}</p> : null}
                    <p className="mt-2 text-xs text-[#1E3A5F]/45">
                      Documentos: {row.documentsAccess ? 'acceso concedido' : 'sin acceso'}
                    </p>
                  </div>
                  <Link to={`/cursos/${row.courseSlug}`} className="text-sm underline">
                    Ver ficha
                  </Link>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {row.status !== 'approved' ? (
                    <button
                      type="button"
                      disabled={busyId === row._id}
                      onClick={() => act(row, { status: 'approved' })}
                      className="bg-[#1E3A5F] px-3 py-2 text-sm text-[#F4EFE6] disabled:opacity-50"
                    >
                      Aceptar en el curso
                    </button>
                  ) : null}
                  {!row.documentsAccess ? (
                    <button
                      type="button"
                      disabled={busyId === row._id}
                      onClick={() => act(row, { status: 'approved', documentsAccess: true })}
                      className="border border-[#1E3A5F] px-3 py-2 text-sm disabled:opacity-50"
                    >
                      Dar acceso a documentos
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busyId === row._id}
                      onClick={() => act(row, { documentsAccess: false })}
                      className="border border-[#1E3A5F]/20 px-3 py-2 text-sm disabled:opacity-50"
                    >
                      Retirar documentos
                    </button>
                  )}
                  {row.status !== 'rejected' ? (
                    <button
                      type="button"
                      disabled={busyId === row._id}
                      onClick={() => act(row, { status: 'rejected', documentsAccess: false })}
                      className="px-3 py-2 text-sm text-[#C41E3A] disabled:opacity-50"
                    >
                      Rechazar
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}

export default Admin
